import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Icon,
  Paper,
  Theme,
  Typography
} from "@outschool/backpack";
import { SearchFilters } from "@outschool/gql-frontend-generated";
import { faBellConcierge } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import React from "react";

import { ConciergeTextInput } from "./ConciergeTextInput";
import {
  CONCIERGE_IN_PAGE_SECTION_USAGE,
  CONCIERGE_TRACKING_EVENTS
} from "./constants";

export function ConciergeInPageSection({
  usage,
  translatedDescription,
  searchFilters
}: {
  // "dashboard" | "membership_purchased_page" | "search_page_show_more" | "search_page_too_specific"
  usage: (typeof CONCIERGE_IN_PAGE_SECTION_USAGE)[keyof typeof CONCIERGE_IN_PAGE_SECTION_USAGE];
  translatedDescription?: React.ReactElement;
  searchFilters?: SearchFilters;
}) {
  const { t } = useTranslation(
    "client\\components\\concierge\\ConciergeInPageSection"
  );

  const trackEvent = useTrackEvent();
  return (
    <Paper
      sx={(theme: Theme) => ({
        padding: "4.8rem 4.8rem",
        borderRadius: "1.6rem",
        backgroundColor: "primary.50",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1.6rem",
        [theme.breakpoints.down("md")]: {
          padding: "1.6rem 1.6rem"
        }
      })}
    >
      <Typography>
        {translatedDescription || (
          <Trans t={t}>
            You don’t have any upcoming classes. Our Concierge team can
            recommend options for your learner. Share details like their
            schedule, interests, or goals, and we’ll send suggestions within{" "}
            <strong>2 business days</strong>.
          </Trans>
        )}
      </Typography>
      <Accordion
        sx={{
          width: "100%",
          color: "primary.main",
          borderRadius: "0.8rem !important",
          boxShadow:
            "0 0 4px 0 rgba(0, 0, 0, 0.04), 0 4px 8px 0 rgba(0, 0, 0, 0.06)"
        }}
        onChange={() => {
          trackEvent(`${CONCIERGE_TRACKING_EVENTS.ACCORDION_CLICKED}_${usage}`);
        }}
      >
        <AccordionSummary sx={{ padding: "0.8rem 1.6rem" }}>
          <Box
            sx={{ margin: "0 1.6rem" }}
            display="flex"
            alignItems="center"
            gap="0.8rem"
          >
            <Icon
              icon={faBellConcierge}
              sx={{ fontSize: "2rem", marginRight: "5px" }}
            />
            <Typography variant="subtitle2">
              {t("Ask our Concierge")}
            </Typography>{" "}
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={(theme: Theme) => ({
            padding: "0 6.4rem 2.4rem 6.4rem",
            [theme.breakpoints.down("md")]: {
              padding: "0 1.6rem 1.6rem 1.6rem"
            }
          })}
        >
          <ConciergeTextInput
            submitTrackingEvent={`${CONCIERGE_TRACKING_EVENTS.REQUEST_SUBMIT_BUTTON_TOUCH}_${usage}`}
            searchFilters={searchFilters}
          />
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
